import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./Admin.css";
import '@fortawesome/fontawesome-free/css/all.min.css';
import base_url from "../../api";

const Admin = () => {
  const navigate = useNavigate();
  const [questionnaires, setQuestionnaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState(null);
  const [referralContacts, setReferralContacts] = useState([]);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [referralFormData, setReferralFormData] = useState({
    firmName: "",
    solicitorName: "",
    contactEmail: "",
    contactPhone: "",
    contactAddress: "",
    notes: "",
  });
  const [statusUpdateLoading, setStatusUpdateLoading] = useState({});
  const [showAdminPasswordModal, setShowAdminPasswordModal] = useState(false);
  const [adminPassword, setAdminPassword] = useState("");
  const [adminPasswordError, setAdminPasswordError] = useState("");
  const [adminPasswordLoading, setAdminPasswordLoading] = useState(false);
  const [adminVerified, setAdminVerified] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }

    // Check if admin password is already verified in this session
    const adminVerifiedSession = sessionStorage.getItem("adminVerified");
    if (adminVerifiedSession === "true") {
      setAdminVerified(true);
      fetchQuestionnaires();
      fetchReferralContacts();
    } else {
      // Show password prompt
      setShowAdminPasswordModal(true);
    }
  }, [navigate]);

  const handleAdminPasswordSubmit = async (e) => {
    e.preventDefault();
    setAdminPasswordError("");
    setAdminPasswordLoading(true);

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${base_url}/verify-admin-password`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ adminPassword }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Store verification and password in sessionStorage
        sessionStorage.setItem("adminVerified", "true");
        sessionStorage.setItem("adminPassword", adminPassword);
        setAdminVerified(true);
        setShowAdminPasswordModal(false);
        setAdminPassword("");
        // Now fetch data
        fetchQuestionnaires();
        fetchReferralContacts();
      } else {
        setAdminPasswordError(data.message || "Invalid admin password");
        setAdminPassword("");
      }
    } catch (err) {
      console.error("Error verifying admin password:", err);
      setAdminPasswordError("Failed to verify admin password. Please try again.");
      setAdminPassword("");
    } finally {
      setAdminPasswordLoading(false);
    }
  };

  const fetchQuestionnaires = async () => {
    try {
      const token = localStorage.getItem("token");
      setLoading(true);
      setError("");

      const adminPassword = sessionStorage.getItem("adminPassword") || "";
      const response = await fetch(`${base_url}/admin/questionnaires`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Admin-Password": adminPassword,
        },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setQuestionnaires(data.questionnaires || []);
      } else {
        // If admin password is invalid, reset and show password modal
        if (response.status === 401 || response.status === 403) {
          sessionStorage.removeItem("adminVerified");
          sessionStorage.removeItem("adminPassword");
          setAdminVerified(false);
          setShowAdminPasswordModal(true);
          setError("Admin access expired. Please verify password again.");
        } else {
          setError(data.message || "Failed to load questionnaires");
        }
      }
    } catch (err) {
      console.error("Error fetching questionnaires:", err);
      setError("Failed to load questionnaires. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchReferralContacts = async () => {
    try {
      const token = localStorage.getItem("token");
      const adminPassword = sessionStorage.getItem("adminPassword") || "";
      const response = await fetch(`${base_url}/admin/referral-contacts`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Admin-Password": adminPassword,
        },
      });

      const data = await response.json();
      if (response.ok && data.success) {
        setReferralContacts(data.contacts || []);
      }
    } catch (err) {
      console.error("Error fetching referral contacts:", err);
    }
  };

  const handleStatusChange = async (referenceNumber, newStatus, referralData = null) => {
    try {
      const token = localStorage.getItem("token");
      setStatusUpdateLoading({ ...statusUpdateLoading, [referenceNumber]: true });

      const adminPassword = sessionStorage.getItem("adminPassword") || "";
      const response = await fetch(
        `${base_url}/admin/questionnaire/${referenceNumber}/status`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-Admin-Password": adminPassword,
          },
          body: JSON.stringify({
            status: newStatus,
            referralData: referralData,
          }),
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        // Refresh questionnaires
        await fetchQuestionnaires();
        alert(`Status updated to ${newStatus}. Email sent to client.`);
      } else {
        // If admin password is invalid, reset and show password modal
        if (response.status === 401 || response.status === 403) {
          sessionStorage.removeItem("adminVerified");
          sessionStorage.removeItem("adminPassword");
          setAdminVerified(false);
          setShowAdminPasswordModal(true);
          alert("Admin access expired. Please verify password again.");
        } else {
          alert(data.message || "Failed to update status");
        }
      }
    } catch (err) {
      console.error("Error updating status:", err);
      alert("Failed to update status. Please try again.");
    } finally {
      setStatusUpdateLoading({ ...statusUpdateLoading, [referenceNumber]: false });
    }
  };

  const handleReferralSubmit = async (referenceNumber) => {
    if (!referralFormData.firmName) {
      alert("Please enter a firm name");
      return;
    }

    await handleStatusChange(referenceNumber, "referred", referralFormData);
    setShowReferralModal(false);
    setReferralFormData({
      firmName: "",
      solicitorName: "",
      contactEmail: "",
      contactPhone: "",
      contactAddress: "",
      notes: "",
    });
  };

  const downloadPDF = async (referenceNumber) => {
    try {
      const token = localStorage.getItem("token");
      const adminPassword = sessionStorage.getItem("adminPassword") || "";
      const response = await fetch(`${base_url}/admin/questionnaire/${referenceNumber}/pdf`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Admin-Password": adminPassword,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Questionnaire_${referenceNumber}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        if (response.status === 401 || response.status === 403) {
          sessionStorage.removeItem("adminVerified");
          sessionStorage.removeItem("adminPassword");
          setAdminVerified(false);
          setShowAdminPasswordModal(true);
          alert("Admin access expired. Please verify password again.");
        } else {
          alert("Failed to download PDF");
        }
      }
    } catch (err) {
      console.error("Error downloading PDF:", err);
      alert("Failed to download PDF");
    }
  };

  const downloadWord = async (referenceNumber) => {
    try {
      const token = localStorage.getItem("token");
      const adminPassword = sessionStorage.getItem("adminPassword") || "";
      const response = await fetch(`${base_url}/admin/questionnaire/${referenceNumber}/word`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Admin-Password": adminPassword,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Questionnaire_${referenceNumber}.docx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        if (response.status === 401 || response.status === 403) {
          sessionStorage.removeItem("adminVerified");
          sessionStorage.removeItem("adminPassword");
          setAdminVerified(false);
          setShowAdminPasswordModal(true);
          alert("Admin access expired. Please verify password again.");
        } else {
          alert("Failed to download Word document");
        }
      }
    } catch (err) {
      console.error("Error downloading Word:", err);
      alert("Failed to download Word document");
    }
  };

  const exportToExcel = async () => {
    try {
      const token = localStorage.getItem("token");
      const adminPassword = sessionStorage.getItem("adminPassword") || "";
      const response = await fetch(`${base_url}/admin/questionnaires/export`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Admin-Password": adminPassword,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Questionnaires_Export_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        if (response.status === 401 || response.status === 403) {
          sessionStorage.removeItem("adminVerified");
          sessionStorage.removeItem("adminPassword");
          setAdminVerified(false);
          setShowAdminPasswordModal(true);
          alert("Admin access expired. Please verify password again.");
        } else {
          alert("Failed to export to Excel");
        }
      }
    } catch (err) {
      console.error("Error exporting to Excel:", err);
      alert("Failed to export to Excel");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatAddress = (address) => {
    if (!address) return "N/A";
    if (typeof address === 'string') return address;
    if (address.fullAddress) return address.fullAddress;
    
    const parts = [];
    if (address.houseNumber) parts.push(address.houseNumber);
    if (address.street) parts.push(address.street);
    if (address.city) parts.push(address.city);
    if (address.country) parts.push(address.country);
    if (address.postalCode) parts.push(address.postalCode);
    
    return parts.length > 0 ? parts.join(", ") : "N/A";
  };

  const getQuestionnaireTypeLabel = (type) => {
    const labels = {
      divorce: "Divorce",
      prenup: "Pre-Nuptial Agreement",
      will: "Will",
    };
    return labels[type] || type;
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      pending: "status-pending",
      accepted: "status-accepted",
      rejected: "status-rejected",
      referred: "status-referred",
    };
    return classes[status] || "status-pending";
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: "Pending",
      accepted: "Accepted",
      rejected: "Rejected",
      referred: "Referred",
    };
    return labels[status] || "Pending";
  };

  // Show password modal if not verified
  if (showAdminPasswordModal && !adminVerified) {
    return (
      <div className="admin-container">
        <div className="admin-password-modal-overlay">
          <div className="admin-password-modal">
            <div className="admin-password-modal-header">
              <h2>
                <i className="fa-solid fa-lock"></i> Admin Access Required
              </h2>
              <p>Please enter the admin password to access this section</p>
            </div>
            <form onSubmit={handleAdminPasswordSubmit} className="admin-password-form">
              <div className="form-group">
                <label>Admin Password</label>
                <input
                  type="password"
                  value={adminPassword}
                  onChange={(e) => {
                    setAdminPassword(e.target.value);
                    setAdminPasswordError("");
                  }}
                  placeholder="Enter admin password"
                  required
                  autoFocus
                />
              </div>
              {adminPasswordError && (
                <div className="admin-password-error">
                  <i className="fa-solid fa-exclamation-circle"></i> {adminPasswordError}
                </div>
              )}
              <button
                type="submit"
                className="admin-password-submit-btn"
                disabled={adminPasswordLoading}
              >
                {adminPasswordLoading ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin"></i> Verifying...
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-unlock"></i> Verify & Access
                  </>
                )}
              </button>
            </form>
            <button
              className="admin-password-cancel-btn"
              onClick={() => {
                navigate("/my-profile");
              }}
            >
              <i className="fa-solid fa-arrow-left"></i> Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>
          <i className="fa-solid fa-briefcase"></i> Admin Center
        </h1>
        <p className="admin-subtitle">Manage completed questionnaires</p>
      </div>

      {loading ? (
        <div className="admin-loading">
          <i className="fa-solid fa-spinner fa-spin"></i> Loading questionnaires...
        </div>
      ) : error ? (
        <div className="admin-error">
          <i className="fa-solid fa-exclamation-circle"></i> {error}
        </div>
      ) : (
        <>
          <div className="admin-actions">
            <button className="btn-export" onClick={exportToExcel}>
              <i className="fa-solid fa-file-excel"></i> Export to Excel
            </button>
          </div>

          <div className="admin-stats">
            <div className="stat-card">
              <div className="stat-icon">
                <i className="fa-solid fa-file-lines"></i>
              </div>
              <div className="stat-content">
                <div className="stat-number">{questionnaires.length}</div>
                <div className="stat-label">Total Completed</div>
              </div>
            </div>
          </div>

          {questionnaires.length === 0 ? (
            <div className="admin-empty">
              <i className="fa-solid fa-inbox"></i>
              <p>No completed questionnaires yet</p>
            </div>
          ) : (
            <div className="questionnaires-table-wrapper">
              <table className="questionnaires-table">
                <thead>
                  <tr>
                    <th>Reference Number</th>
                    <th>Individual Name</th>
                    <th>Address</th>
                    <th>Phone</th>
                    <th>Email</th>
                    <th>Date Initiated</th>
                    <th>Date Completed</th>
                    <th>Date Submitted</th>
                    <th>Application Files</th>
                    <th>Client Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {questionnaires.map((q) => (
                    <tr key={q.id}>
                      <td>
                        <strong className="ref-number">{q.referenceNumber}</strong>
                      </td>
                      <td>{q.client.name}</td>
                      <td className="address-cell">{formatAddress(q.client.address)}</td>
                      <td>{q.client.phone || "N/A"}</td>
                      <td>
                        <a href={`mailto:${q.client.email}`}>{q.client.email}</a>
                      </td>
                      <td>{formatDateShort(q.dateInitiated)}</td>
                      <td>{formatDateShort(q.dateCompleted)}</td>
                      <td>{formatDateShort(q.dateSubmitted)}</td>
                      <td>
                        <div className="file-buttons">
                          <button
                            className="btn-file btn-word"
                            onClick={() => downloadWord(q.referenceNumber)}
                            title="Download Word"
                          >
                            <i className="fa-solid fa-file-word"></i>
                          </button>
                          <button
                            className="btn-file btn-pdf"
                            onClick={() => downloadPDF(q.referenceNumber)}
                            title="Download PDF"
                          >
                            <i className="fa-solid fa-file-pdf"></i>
                          </button>
                        </div>
                      </td>
                      <td>
                        <select
                          className={`status-select ${getStatusBadgeClass(q.clientStatus)}`}
                          value={q.clientStatus}
                          onChange={(e) => {
                            const newStatus = e.target.value;
                            if (newStatus === "referred") {
                              setSelectedQuestionnaire(q);
                              setShowReferralModal(true);
                            } else {
                              handleStatusChange(q.referenceNumber, newStatus);
                            }
                          }}
                          disabled={statusUpdateLoading[q.referenceNumber]}
                        >
                          <option value="pending">Pending</option>
                          <option value="accepted">Accepted</option>
                          <option value="rejected">Rejected</option>
                          <option value="referred">Referred</option>
                        </select>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn-view"
                            onClick={() => setSelectedQuestionnaire(q)}
                            title="View Details"
                          >
                            <i className="fa-solid fa-eye"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Questionnaire Detail Modal */}
          {selectedQuestionnaire && !showReferralModal && (
            <div className="modal-overlay" onClick={() => setSelectedQuestionnaire(null)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>Questionnaire Details</h2>
                  <button
                    className="modal-close"
                    onClick={() => setSelectedQuestionnaire(null)}
                  >
                    <i className="fa-solid fa-times"></i>
                  </button>
                </div>
                <div className="modal-body">
                  <div className="detail-section">
                    <h3>Client Information</h3>
                    <p><strong>Name:</strong> {selectedQuestionnaire.client.name}</p>
                    <p><strong>Email:</strong> {selectedQuestionnaire.client.email}</p>
                    {selectedQuestionnaire.client.phone && (
                      <p><strong>Phone:</strong> {selectedQuestionnaire.client.phone}</p>
                    )}
                    <p><strong>Address:</strong> {formatAddress(selectedQuestionnaire.client.address)}</p>
                  </div>
                  <div className="detail-section">
                    <h3>Questionnaire Information</h3>
                    <p><strong>Reference Number:</strong> {selectedQuestionnaire.referenceNumber}</p>
                    <p><strong>Type:</strong> {getQuestionnaireTypeLabel(selectedQuestionnaire.questionnaireType)}</p>
                    <p><strong>Date Initiated:</strong> {formatDate(selectedQuestionnaire.dateInitiated)}</p>
                    <p><strong>Date Completed:</strong> {formatDate(selectedQuestionnaire.dateCompleted)}</p>
                    <p><strong>Date Submitted:</strong> {formatDate(selectedQuestionnaire.dateSubmitted)}</p>
                    <p><strong>Status:</strong> <span className={getStatusBadgeClass(selectedQuestionnaire.clientStatus)}>{getStatusLabel(selectedQuestionnaire.clientStatus)}</span></p>
                  </div>
                  <div className="detail-section">
                    <h3>Answers</h3>
                    <div className="answers-list">
                      {Object.entries(selectedQuestionnaire.answers || {}).map(([key, value]) => (
                        <div key={key} className="answer-item">
                          <strong>{key}:</strong>{" "}
                          <span>
                            {Array.isArray(value) ? value.join(", ") : String(value || "N/A")}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    className="btn-download"
                    onClick={() => {
                      downloadWord(selectedQuestionnaire.referenceNumber);
                    }}
                  >
                    <i className="fa-solid fa-file-word"></i> Download Word
                  </button>
                  <button
                    className="btn-download"
                    onClick={() => {
                      downloadPDF(selectedQuestionnaire.referenceNumber);
                    }}
                  >
                    <i className="fa-solid fa-file-pdf"></i> Download PDF
                  </button>
                  <button
                    className="btn-close"
                    onClick={() => setSelectedQuestionnaire(null)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Referral Modal */}
          {showReferralModal && selectedQuestionnaire && (
            <div className="modal-overlay" onClick={() => setShowReferralModal(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>Refer Client</h2>
                  <button
                    className="modal-close"
                    onClick={() => {
                      setShowReferralModal(false);
                      setReferralFormData({
                        firmName: "",
                        solicitorName: "",
                        contactEmail: "",
                        contactPhone: "",
                        contactAddress: "",
                        notes: "",
                      });
                    }}
                  >
                    <i className="fa-solid fa-times"></i>
                  </button>
                </div>
                <div className="modal-body">
                  <p style={{ marginBottom: "20px", color: "#374151" }}>
                    Please provide referral details for <strong>{selectedQuestionnaire.client.name}</strong>
                  </p>

                  {referralContacts.length > 0 && (
                    <div className="referral-contacts-section">
                      <label style={{ display: "block", marginBottom: "8px", fontWeight: "600" }}>
                        Or select from existing contacts:
                      </label>
                      <select
                        className="form-select"
                        onChange={(e) => {
                          const contact = referralContacts.find(c => c.id === parseInt(e.target.value));
                          if (contact) {
                            setReferralFormData({
                              firmName: contact.firm_name || "",
                              solicitorName: contact.solicitor_name || "",
                              contactEmail: contact.contact_email || "",
                              contactPhone: contact.contact_phone || "",
                              contactAddress: contact.contact_address || "",
                              notes: contact.notes || "",
                            });
                          }
                        }}
                        style={{ width: "100%", padding: "8px", marginBottom: "20px" }}
                      >
                        <option value="">Select existing contact...</option>
                        {referralContacts.map((contact) => (
                          <option key={contact.id} value={contact.id}>
                            {contact.firm_name} {contact.solicitor_name ? `- ${contact.solicitor_name}` : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="form-group">
                    <label>Firm Name *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={referralFormData.firmName}
                      onChange={(e) => setReferralFormData({ ...referralFormData, firmName: e.target.value })}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Solicitor Name</label>
                    <input
                      type="text"
                      className="form-input"
                      value={referralFormData.solicitorName}
                      onChange={(e) => setReferralFormData({ ...referralFormData, solicitorName: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label>Contact Email</label>
                    <input
                      type="email"
                      className="form-input"
                      value={referralFormData.contactEmail}
                      onChange={(e) => setReferralFormData({ ...referralFormData, contactEmail: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label>Contact Phone</label>
                    <input
                      type="tel"
                      className="form-input"
                      value={referralFormData.contactPhone}
                      onChange={(e) => setReferralFormData({ ...referralFormData, contactPhone: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label>Contact Address</label>
                    <textarea
                      className="form-textarea"
                      value={referralFormData.contactAddress}
                      onChange={(e) => setReferralFormData({ ...referralFormData, contactAddress: e.target.value })}
                      rows="3"
                    />
                  </div>

                  <div className="form-group">
                    <label>Notes</label>
                    <textarea
                      className="form-textarea"
                      value={referralFormData.notes}
                      onChange={(e) => setReferralFormData({ ...referralFormData, notes: e.target.value })}
                      rows="3"
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    className="btn-primary"
                    onClick={() => handleReferralSubmit(selectedQuestionnaire.referenceNumber)}
                    disabled={statusUpdateLoading[selectedQuestionnaire.referenceNumber]}
                  >
                    {statusUpdateLoading[selectedQuestionnaire.referenceNumber] ? "Processing..." : "Submit Referral"}
                  </button>
                  <button
                    className="btn-close"
                    onClick={() => {
                      setShowReferralModal(false);
                      setReferralFormData({
                        firmName: "",
                        solicitorName: "",
                        contactEmail: "",
                        contactPhone: "",
                        contactAddress: "",
                        notes: "",
                      });
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Admin;
